import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import {
  CONTENT_PANEL_STYLE,
  FONT_FAMILY,
  PRIMARY_BUTTON_STYLE,
  setButtonEnabled,
  TYPO,
} from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import type { AutoPlaySnapshot } from "../scenes/GameScene.js";
import type { TabId } from "./BottomTabView.js";
import type { AdFormat } from "@yjsgames/ad-ports";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";

export interface DebugDashboardModalOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly getSnapshot: () => AutoPlaySnapshot;
  readonly onClose: () => void;
  readonly onOpenTab: (tab: TabId) => void;
  readonly onAddGold: (n: number) => void;
  readonly onAddDiamond: (n: number) => void;
  readonly onSetChapter: (n: number) => void;
  readonly onForceDailyReset: () => void;
  readonly onGrantAllRelics: () => void;
  readonly onChallengeDungeon: () => void;
  readonly onSweepDungeon: () => void;
  /** 인하우스 광고 강제 토글 — 새 상태(true=ON) 반환. 모바일에서도 자사광고 100%. */
  readonly onToggleForceHouseAd: () => boolean;
  /** 모달 열 때의 인하우스 강제 현재 상태. */
  readonly forceHouseAdInitial: boolean;
  /** 광고 미리보기 — 대시보드를 닫고 광고를 즉시 띄운다(QA). reward=보상형 / interstitial=전면. */
  readonly onPreviewHouseAd: (format: AdFormat) => void;
}

const PW = 356;
const PH = 704;

/** 상태 텍스트 행 style (소형 고정폭). */
const ROW_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  fontFamily: FONT_FAMILY,
  fontSize: TYPO.tiny,
  color: PALETTE.textSecondary,
};

/** 섹션 헤더 style. */
const HDR_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  fontFamily: FONT_FAMILY,
  fontSize: TYPO.tiny,
  color: PALETTE.accentGold,
};

/** 긴 문자열을 maxLen자로 잘라 '…' 추가. */
function trunc(s: string, maxLen = 28): string {
  return s.length <= maxLen ? s : s.slice(0, maxLen - 1) + "…";
}

/**
 * QA 대시보드 모달 — CHEAT_ENABLED(dev flavor) 환경에서만 열린다.
 * 상단: 기본 자원·챕터·DPS 상태 요약.
 * 중단: 그리드/경로, 던전, 일일상점, 유물 보너스 텍스트 행.
 * 하단: 탭 이동·자원 조작·게임 제어 버튼 그리드.
 */
export class DebugDashboardModal extends ModalLayer {
  private readonly opts: DebugDashboardModalOptions;
  private readonly cx: number;
  private readonly cy: number;

  // 동적 갱신 텍스트 행 참조
  private readonly statusText: Phaser.GameObjects.Text;
  private readonly gridText: Phaser.GameObjects.Text;
  private readonly dungeonText: Phaser.GameObjects.Text;
  private readonly shopText: Phaser.GameObjects.Text;
  private readonly relicText: Phaser.GameObjects.Text;

  // 상태 의존 버튼
  private readonly challengeBtn: NineSliceButton;
  private readonly sweepBtn: NineSliceButton;

  constructor(scene: Phaser.Scene, opts: DebugDashboardModalOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.80,
      onBackdropClick: () => opts.onClose(),
    });

    this.opts = opts;
    this.cx = opts.screenWidth / 2;
    this.cy = opts.screenHeight / 2;

    const { cx, cy } = this;
    // dim 클릭 닫기 — 패널 영역은 제외(패널 내부 클릭은 안 닫힘).
    this.setBackdropCloseExclusion(cx - PW / 2, cy - PH / 2, PW, PH);

    // ── 패널 ──────────────────────────────────────────────
    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: PW,
      height: PH,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    // ── 제목 + 닫기 ───────────────────────────────────────
    const title = scene.add
      .text(cx, cy - PH / 2 + 22, "QA 대시보드 (DEV)", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.subtitle,
        color: PALETTE.accentGold,
      })
      .setOrigin(0.5);
    this.contentContainer.add(title);

    const closeBtn = scene.add
      .text(cx + PW / 2 - 18, cy - PH / 2 + 16, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => opts.onClose());
    this.contentContainer.add(closeBtn);

    // ── 상태 섹션 ─────────────────────────────────────────
    const lx = cx - PW / 2 + 16;
    let ry = cy - PH / 2 + 46;

    this.addHdr(scene, lx, ry, "▸ 자원 / 챕터");
    ry += 14;

    this.statusText = scene.add.text(lx, ry, "", ROW_STYLE).setOrigin(0, 0);
    this.contentContainer.add(this.statusText);
    ry += 52;

    this.addHdr(scene, lx, ry, "▸ 그리드 / 경로");
    ry += 14;

    this.gridText = scene.add.text(lx, ry, "", ROW_STYLE).setOrigin(0, 0);
    this.contentContainer.add(this.gridText);
    ry += 38;

    this.addHdr(scene, lx, ry, "▸ 던전");
    ry += 14;

    this.dungeonText = scene.add.text(lx, ry, "", ROW_STYLE).setOrigin(0, 0);
    this.contentContainer.add(this.dungeonText);
    ry += 38;

    this.addHdr(scene, lx, ry, "▸ 일일상점");
    ry += 14;

    this.shopText = scene.add.text(lx, ry, "", ROW_STYLE).setOrigin(0, 0);
    this.contentContainer.add(this.shopText);
    ry += 38;

    this.addHdr(scene, lx, ry, "▸ 유물 보너스");
    ry += 14;

    this.relicText = scene.add.text(lx, ry, "", ROW_STYLE).setOrigin(0, 0);
    this.contentContainer.add(this.relicText);
    ry += 30;

    // ── 구분선 ────────────────────────────────────────────
    const divider = scene.add.graphics();
    divider.lineStyle(1, PALETTE_NUM.panelDivider, 0.7);
    divider.lineBetween(cx - PW / 2 + 12, ry, cx + PW / 2 - 12, ry);
    this.contentContainer.add(divider);
    ry += 8;

    // ── 버튼 그리드 ───────────────────────────────────────
    // 버튼 정의: [라벨, 너비, 액션]
    type BtnDef = [string, number, () => void];

    const BTN_H = 28;
    const COL2_W = 100; // 2열 버튼 너비
    const COL3_W = 84;  // 3열 버튼 너비 (3개)

    // 행1: 탭 이동 (Battle / Shop / Dungeon)
    const tabRow: BtnDef[] = [
      ["전투탭", COL3_W, () => { opts.onOpenTab("battle"); opts.onClose(); }],
      ["상점탭", COL3_W, () => { opts.onOpenTab("shop"); opts.onClose(); }],
      ["던전탭", COL3_W, () => { opts.onOpenTab("dungeon"); opts.onClose(); }],
    ];

    // 행2: 자원 조작 (+1K Gold / +100 Dia / Next Chapter)
    const resourceRow: BtnDef[] = [
      ["+1K 골드", COL3_W, () => { opts.onAddGold(1000); this.refresh(); }],
      ["+100 다이아", COL3_W, () => { opts.onAddDiamond(100); this.refresh(); }],
      ["다음 챕터", COL3_W, () => { opts.onSetChapter(opts.getSnapshot().chapter + 1); this.refresh(); }],
    ];

    // 행3: 게임 제어 2열 (Daily Reset / All Relics)
    const ctrlRow1: BtnDef[] = [
      ["일일 리셋", COL2_W, () => { opts.onForceDailyReset(); this.refresh(); }],
      ["유물 전부", COL2_W, () => { opts.onGrantAllRelics(); this.refresh(); }],
    ];

    // 행4: 갱신 + 던전 (Refresh / Challenge / Sweep)
    // Challenge / Sweep 은 상태 의존이므로 별도 참조 보관

    const rows: BtnDef[][] = [tabRow, resourceRow, ctrlRow1];
    const ROW_GAP = BTN_H + 6;

    rows.forEach((row, ri) => {
      const totalW = row.reduce((s, [, w]) => s + w, 0) + (row.length - 1) * 6;
      let bx = cx - totalW / 2;
      row.forEach(([label, w, action]) => {
        const btn = new NineSliceButton(scene, bx + w / 2, ry + ri * ROW_GAP + BTN_H / 2, {
          style: PRIMARY_BUTTON_STYLE,
          label,
          widthOverride: w,
          heightOverride: BTN_H,
        });
        btn.on(NINE_SLICE_BUTTON_EVENT.Click, action);
        this.contentContainer.add(btn);
        bx += w + 6;
      });
    });

    ry += rows.length * ROW_GAP + 4;

    // 행4: Refresh + Challenge + Sweep
    const lastRowY = ry + BTN_H / 2;
    const refreshBtn = new NineSliceButton(scene, cx - 108, lastRowY, {
      style: PRIMARY_BUTTON_STYLE,
      label: "갱신",
      widthOverride: 72,
      heightOverride: BTN_H,
    });
    refreshBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.refresh());
    this.contentContainer.add(refreshBtn);

    this.challengeBtn = new NineSliceButton(scene, cx + 4, lastRowY, {
      style: PRIMARY_BUTTON_STYLE,
      label: "도전",
      widthOverride: 84,
      heightOverride: BTN_H,
    });
    this.challengeBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
      opts.onChallengeDungeon();
      this.refresh();
    });
    this.contentContainer.add(this.challengeBtn);

    this.sweepBtn = new NineSliceButton(scene, cx + 104, lastRowY, {
      style: PRIMARY_BUTTON_STYLE,
      label: "소탕",
      widthOverride: 84,
      heightOverride: BTN_H,
    });
    this.sweepBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
      opts.onSweepDungeon();
      this.refresh();
    });
    this.contentContainer.add(this.sweepBtn);

    // 인하우스 광고 강제 토글 (QA — 모바일에서도 자사광고 100%, 세션 한정)
    const houseAdY = lastRowY + ROW_GAP;
    let forceOn = opts.forceHouseAdInitial;
    const houseAdBtn = new NineSliceButton(scene, cx - 56, houseAdY, {
      style: PRIMARY_BUTTON_STYLE,
      label: "인하우스광고 강제",
      widthOverride: 148,
      heightOverride: BTN_H,
    });
    const houseAdState = scene.add
      .text(cx + 44, houseAdY, forceOn ? "ON" : "OFF", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.tiny,
        color: forceOn ? PALETTE.accentGold : PALETTE.textSecondary,
      })
      .setOrigin(0, 0.5);
    houseAdBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
      forceOn = opts.onToggleForceHouseAd();
      houseAdState.setText(forceOn ? "ON" : "OFF");
      houseAdState.setColor(forceOn ? PALETTE.accentGold : PALETTE.textSecondary);
    });
    this.contentContainer.add(houseAdBtn);
    this.contentContainer.add(houseAdState);

    // 광고 미리보기 — 보상형(라우팅) / 전면(자사 interstitial) 둘 다 즉시 표시(QA)
    const rewardPreviewBtn = new NineSliceButton(scene, cx - 56, houseAdY + ROW_GAP, {
      style: PRIMARY_BUTTON_STYLE,
      label: "보상형",
      widthOverride: 100,
      heightOverride: BTN_H,
    });
    rewardPreviewBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onPreviewHouseAd("reward"));
    this.contentContainer.add(rewardPreviewBtn);

    const interstitialPreviewBtn = new NineSliceButton(scene, cx + 56, houseAdY + ROW_GAP, {
      style: PRIMARY_BUTTON_STYLE,
      label: "전면",
      widthOverride: 100,
      heightOverride: BTN_H,
    });
    interstitialPreviewBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onPreviewHouseAd("interstitial"));
    this.contentContainer.add(interstitialPreviewBtn);

    // 초기 렌더
    this.refresh();
  }

  /** 스냅샷을 다시 읽어 상태 텍스트 행과 버튼 활성 상태를 갱신한다. */
  public refresh(): void {
    const s = this.opts.getSnapshot();

    // 자원 / 챕터
    this.statusText.setText([
      `골드: ${s.gold.toLocaleString()}  다이아: ${s.diamond.toLocaleString()}  강화석: ${s.stone.toLocaleString()}`,
      `챕터: ${s.chapter}  최고: ${s.bestChapter}  환생P: ${s.rebirthPoint}`,
      `기본공격: ${s.baseDamage.toFixed(1)}  DPS: ${s.dps.toFixed(1)}`,
      `활성탭: ${s.activeTab}  벽돌: ${s.activeBricks}`,
    ]);

    // 그리드 / 경로
    const pathIds = s.pathDefinitionIds.length
      ? trunc(s.pathDefinitionIds.join("→"), 40)
      : "(없음)";
    this.gridText.setText([
      `배치: ${s.placed.length}개  도감해금: ${s.unlockedCount}  연결: ${s.pathConnected ? "✓" : "✗"}`,
      `경로: ${pathIds}`,
      trunc(`스펙: ${s.specSummary}`, 44),
    ]);

    // 던전
    const msR = s.dungeonMsUntilReset;
    const resetIn = msR > 0 ? `${Math.ceil(msR / 1000 / 60)}분` : "-";
    this.dungeonText.setText([
      `인덱스: ${s.dungeonIndex}  클리어: ${s.dungeonClearedMax}  도전권: ${s.dungeonTickets}/${s.dungeonTicketLimit}`,
      `리셋까지: ${resetIn}  도전: ${s.dungeonCanChallenge ? "가능" : "불가"}  소탕: ${s.dungeonCanSweep ? "가능" : "불가"}`,
    ]);

    // 일일상점
    const ds = s.dailyShop;
    const soldOutCount = ds.slots.filter(sl => sl?.soldOut).length;
    this.shopText.setText([
      `품절: ${soldOutCount}/${ds.slots.length}  리롤: ${ds.rerollLabel}`,
      `리셋까지: ${ds.msUntilReset > 0 ? `${Math.ceil(ds.msUntilReset / 1000 / 60)}분` : "-"}  가능: ${ds.rerollAvailable ? "✓" : "✗"}`,
    ]);

    // 유물 보너스
    const rb = s.relicBonuses;
    this.relicText.setText([
      `보유유물: ${s.ownedRelicCount}종  공격: +${rb.attackPercent.toFixed(0)}%  발사속도: +${rb.fireRatePercent.toFixed(0)}%`,
      `골드: +${rb.goldGainPercent.toFixed(0)}%  발사체속도: +${rb.projectileSpeedPercent.toFixed(0)}%`,
    ]);

    // 던전 버튼 활성 상태
    setButtonEnabled(this.challengeBtn, s.dungeonCanChallenge);
    setButtonEnabled(this.sweepBtn, s.dungeonCanSweep);
  }

  /** 씬 텍스트 행 헤더 추가 헬퍼 (내부용). */
  private addHdr(scene: Phaser.Scene, x: number, y: number, label: string): void {
    const t = scene.add.text(x, y, label, HDR_STYLE).setOrigin(0, 0);
    this.contentContainer.add(t);
  }
}
